import { useMemo } from 'react';
import { TileLayer, H3HexagonLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import { scaleLinear } from 'd3-scale';
import { H3PriceData, PriceRange } from '../../types';

interface UseMapLayersProps {
  filteredH3Data: H3PriceData[];
  priceRange: PriceRange;
  coverage: number;
  opacity: number;
  onHexHover: (object?: H3PriceData) => void;
  hoveredHex?: H3PriceData | null;
}

// Fixed Carto Dark basemap URL
const DARK_BASEMAP_URL = 'https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png';

export function useMapLayers({ 
  filteredH3Data, 
  priceRange, 
  coverage, 
  opacity, 
  onHexHover,
  hoveredHex
}: UseMapLayersProps) {
  const colorScaleFunc = useMemo(() => {
    return scaleLinear<string>()
      .domain([priceRange.min, priceRange.max])
      .range(['rgba(41, 121, 255, 0.8)', 'rgba(255, 87, 51, 0.8)'])
      .clamp(true);
  }, [priceRange]);

  const layers = useMemo(() => {
    const baseLayers = [];
    
    // Fixed Carto Dark basemap
    baseLayers.push(new TileLayer({
      id: 'carto-dark-basemap',
      data: DARK_BASEMAP_URL,
      minZoom: 0,
      maxZoom: 18,
      tileSize: 256,
      renderSubLayers: (props: any) => {
        return new BitmapLayer({
          ...props,
          id: `tile-${props.tile.index.x}-${props.tile.index.y}-${props.tile.index.z}`,
          data: undefined,
          image: props.data,
          bounds: [
            props.tile.bbox.west,
            props.tile.bbox.south, 
            props.tile.bbox.east,
            props.tile.bbox.north
          ]
        });
      }
    }));

    // Add smart area labels - never street names, with zoom-based opacity
    baseLayers.push(new TileLayer({
      id: 'area-names-smart',
      data: 'https://cartodb-basemaps-a.global.ssl.fastly.net/dark_only_labels/{z}/{x}/{y}.png',
      minZoom: 8,   // Start at zoom 8 for districts
      maxZoom: 14,  // Stop at 14 to avoid street names completely
      tileSize: 256,
      renderSubLayers: (props: any) => {

        return new BitmapLayer({
          ...props,
          id: `smart-labels-${props.tile.index.x}-${props.tile.index.y}-${props.tile.index.z}`,
          data: undefined,
          image: props.data,
          bounds: [
            props.tile.bbox.west,
            props.tile.bbox.south, 
            props.tile.bbox.east,
            props.tile.bbox.north
          ],
        });
      }
    }));

    return [
      ...baseLayers,
    
      // H3 hexagon layer with 3D extrusion - matching deck.gl example
      new H3HexagonLayer({
        id: 'h3-hexagon-layer-extreme',
        data: filteredH3Data,
        pickable: true,
        wireframe: false,
        filled: true,
        extruded: true,
        coverage: coverage,
        elevationScale: 60, // Fixed elevation scale
        updateTriggers: {
          getFillColor: [opacity, hoveredHex]
        },
        getHexagon: (d: H3PriceData) => d.h3_index_7,
        getElevation: (d: H3PriceData) => {
          const percentile = 1 - d.median_price_per_m2_percent_rank;
          const height = Math.pow(percentile, 5.0) * 100; // Fixed height curve for extreme emphasis
          return height;
        },
        getFillColor: (d: H3PriceData) => {
          const isHovered = hoveredHex?.h3_index_7 === d.h3_index_7;
          const alpha = Math.round(opacity * 255);
          
          if (isHovered) {
            return [255, 255, 0, alpha];
          }
          
          const color = colorScaleFunc(d.median_price_per_m2);
          const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
          if (rgbaMatch) {
            const baseAlpha = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1;
            return [
              parseInt(rgbaMatch[1]), 
              parseInt(rgbaMatch[2]), 
              parseInt(rgbaMatch[3]), 
              Math.round(baseAlpha * alpha)
            ];
          }
          return [128, 128, 128, alpha];
        },
        getLineColor: [255, 255, 255, 80],
        lineWidthMinPixels: 2,
        material: {
          ambient: 0.64,
          diffuse: 0.6,
          shininess: 32,
          specularColor: [51, 51, 51]
        },
        onHover: ({object}: {object?: H3PriceData}) => {
          if (object) {
            const percentile = 1 - object.median_price_per_m2_percent_rank;
            const height = Math.pow(percentile, 5.0) * 100;
            console.log('[H3 Debug]', {
              h3_index: object.h3_index_7,
              price_per_m2: object.median_price_per_m2,
              count: object.count,
              percentile: percentile.toFixed(3),
              height_units: height.toFixed(2),
              elevation_scale: 60,
              curve_exponent: 5.0,
              opacity: opacity.toFixed(2)
            });
          }
          onHexHover(object);
        },
      })
    ];
  }, [filteredH3Data, colorScaleFunc, coverage, opacity, onHexHover, hoveredHex]);

  return { layers, colorScaleFunc };
}
